"use client";

import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useCallback } from "react";
import { type StepId, useCheckoutStore } from "@/lib/store/checkout";
import { cn } from "@/lib/utils";
import { HeroIcon, type HeroIconName } from "../hero-icon";
import { Skeleton } from "../ui/skeleton";

type StepMeta = {
  iconName: HeroIconName;
};

const STEP_META: Record<StepId, StepMeta> = {
  payment: {
    iconName: "credit-card",
  },
  shipping: {
    iconName: "home",
  },
  billing: {
    iconName: "building-office",
  },
  customer: {
    iconName: "user",
  },
};

export const Stepper = () => {
  const t = useTranslations("stepper");
  const steps = useCheckoutStore((s) => s.steps);
  const currentStep = useCheckoutStore((s) => s.currentStep);
  const stepStatus = useCheckoutStore((s) => s.stepStatus);
  const setCurrentStep = useCheckoutStore((s) => s.setCurrentStep);

  const currentIndex = steps.indexOf(currentStep);
  const totalWidth = (steps.length - 1) * 5 + 2;
  const revealWidth = currentIndex * 5;
  const clipRight = (1 - revealWidth / totalWidth) * 100;

  const handleSetCurrentStep = useCallback(
    (step: StepId) => {
      if (stepStatus[step] !== "completed") return;
      setCurrentStep(step);
    },
    [stepStatus, setCurrentStep],
  );

  if (steps.length <= 1) return null;

  return (
    <div className="relative flex w-full items-center justify-center">
      <div className="flex">
        {steps.map((step, index) => {
          const isLast = index + 1 === steps.length;
          const meta = STEP_META[step];
          const active = currentStep === step;
          const completed = stepStatus[step] === "completed";

          const circle = (
            <button
              type="button"
              onClick={() => handleSetCurrentStep(step)}
              key={`circ-${step}`}
              className={cn(
                "relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-muted transition-all duration-300 ease-in-out",
                active &&
                  "bg-neutral-100 text-foreground shadow-sm ring-1 ring-accent/60",
                completed && "cursor-pointer",
              )}
            >
              <AnimatePresence initial={false} mode="popLayout">
                {active && (
                  <motion.span
                    key={`label-${step}`}
                    className="-top-5 absolute font-medium text-xs"
                    initial={{
                      opacity: 0,
                      transform: "translateY(16px) scale(0.5)",
                    }}
                    animate={{
                      opacity: 1,
                      transform: "translateY(0px) scale(1)",
                      transition: {
                        delay: 0.1,
                        duration: 0.16,
                        ease: [0.165, 0.84, 0.44, 1],
                      },
                    }}
                    exit={{
                      opacity: 0,
                      transform: "translateY(16px) scale(0.5)",
                    }}
                    transition={{
                      duration: 0.16,
                      ease: [0.165, 0.84, 0.44, 1],
                    }}
                  >
                    {t(step)}
                  </motion.span>
                )}
              </AnimatePresence>
              <HeroIcon
                name={meta.iconName}
                className="h-4 w-4"
                iconStyle="micro"
              />
            </button>
          );

          return isLast ? (
            circle
          ) : (
            <div key={step} className="relative flex w-20 items-center">
              {circle}
              <div className="h-1 w-full grow scale-x-[1.05] bg-zinc-200" />
            </div>
          );
        })}
      </div>
      <div
        className="pointer-events-none absolute top-0 z-20 flex transition-[clip-path] duration-300 ease-in-out"
        style={{ clipPath: `inset(0 ${clipRight}% 0 0)` }}
      >
        {steps.map((step, index) => {
          const isLast = index + 1 === steps.length;

          const circle = (
            <div
              key={`circ-clip-${step}`}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent"
            >
              <HeroIcon
                name="check"
                className="h-4 w-4 text-neutral-50"
                iconStyle="micro"
              />
            </div>
          );

          return isLast ? (
            circle
          ) : (
            <div
              key={`clip-${step}`}
              className="relative flex w-20 items-center"
            >
              {circle}
              <div className="h-1 w-full grow scale-x-[1.05] bg-accent" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const StepperSkeleton = ({ count = 3 }: { count?: number }) => {
  if (count <= 1) return null;

  return (
    <div className="relative flex w-full items-center justify-center">
      <div className="flex">
        {Array.from({ length: count }, (_, index) => {
          const isLast = index + 1 === count;
          return isLast ? (
            <Skeleton
              // biome-ignore lint/suspicious/noArrayIndexKey: identical static skeleton items
              key={`sk-step-${index}`}
              className="h-8 w-8 rounded-full"
            />
          ) : (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: identical static skeleton items
              key={`sk-step-${index}`}
              className="relative flex w-20 items-center"
            >
              <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
              <Skeleton className="h-1 w-full grow scale-x-[1.05]" />
            </div>
          );
        })}
      </div>
    </div>
  );
};
